// src/containers/Hub/components/MediaCard.tsx
import React, { useState } from 'react';
import { Box, Button, Divider, Theme, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import VideogameAssetOutlinedIcon from '@mui/icons-material/VideogameAssetOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import SearchMedia from './SearchMedia';
import ParentsGuideMedia from './ParentsGuideMedia';
import { ROUTES } from '../../../constants';
import { brand, gradients } from '../../../theme/colors';

// Quiet tonal tiles: the gradient is reserved for real actions like Add Game,
// so here it only colours the icons and a short line along the bottom that
// sweeps full width on hover. No mask-composite border (Safari sliver).
const ICON_GRADIENT_ID = 'media-icon-gradient';

const buttonSx = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    position: 'relative',
    overflow: 'hidden',
    justifyContent: 'flex-start',
    textAlign: 'left' as const,
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 600,
    minHeight: 40,
    px: 1.5,
    py: 0.75,
    color: 'text.primary',
    bgcolor: isDark ? 'rgba(255,255,255,0.045)' : 'rgba(80,60,140,0.05)',
    border: '1px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s, transform 0.2s',
    '& .MuiButton-startIcon svg': { fill: `url(#${ICON_GRADIENT_ID})` },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      backgroundImage: gradients.primary,
      opacity: 0.55,
      transformOrigin: 'left',
      transform: 'scaleX(0.28)',
      transition: 'transform 0.35s ease, opacity 0.35s ease',
    },
    '&:hover, &:focus-visible': {
      bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,60,140,0.1)',
      transform: 'translateY(-1px)',
      '&::after': { transform: 'scaleX(1)', opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::after': { transition: 'none' },
      '&:hover, &:focus-visible': { transform: 'none' },
    },
  };
};

// One shared gradient the icons fill with (CSS gradients can't fill SVG).
const IconGradient = () => (
  <svg width="0" height="0" aria-hidden style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id={ICON_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={brand.pink} />
        <stop offset="50%" stopColor={brand.purple} />
        <stop offset="100%" stopColor={brand.blue} />
      </linearGradient>
    </defs>
  </svg>
);

const groupSx = {
  flex: 1,
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '1fr' },
  // Groups hold different numbers of tiles; keep every tile the same height.
  alignContent: 'start',
  gap: 1.25,
};

// MUI Divider orientation isn't responsive, so render both and toggle by breakpoint.
const GroupDivider = () => (
  <>
    <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
    <Divider
      orientation="vertical"
      flexItem
      sx={{ display: { xs: 'none', md: 'block' } }}
    />
  </>
);

const MediaCard = () => {
  const [showSearchMedia, setShowSearchMedia] = useState(false);
  const [showParentsGuideMedia, setShowParentsGuideMedia] = useState(false);

  return (
    <>
      <IconGradient />

      <Typography
        variant="h6"
        gutterBottom
        color="primary"
        component={Link}
        to={ROUTES.mediaLists}
        sx={{ textDecoration: 'none' }}
        >
        Media Lists
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        <Box sx={groupSx}>
          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.watchlist}
            startIcon={<BookmarkBorderOutlinedIcon />} sx={buttonSx}
          >
            Watchlist
          </Button>

          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.tvShows}
            startIcon={<TvOutlinedIcon />} sx={buttonSx}
          >
            TV Watchlist
          </Button>

          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.gameList}
            startIcon={<VideogameAssetOutlinedIcon />} sx={buttonSx}
          >
            Game List
          </Button>

          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.comicShelf}
            startIcon={<AutoStoriesOutlinedIcon />} sx={buttonSx}
          >
            Comic Shelf
          </Button>
        </Box>

        <GroupDivider />

        <Box sx={groupSx}>
          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.physicalMovieCollection}
            startIcon={<MovieOutlinedIcon />} sx={buttonSx}
          >
            My Physical Movies
          </Button>

          <Button
            variant="text" color="inherit" component={Link} to={ROUTES.physicalGameCollection}
            startIcon={<SportsEsportsOutlinedIcon />} sx={buttonSx}
          >
            My Physical Games
          </Button>
        </Box>

        <GroupDivider />

        <Box sx={groupSx}>
          <Button
            variant="text" color="inherit" onClick={() => setShowSearchMedia(true)}
            startIcon={<LiveTvOutlinedIcon />} sx={buttonSx}
          >
            Where to Stream
          </Button>

          <Button
            variant="text" color="inherit" onClick={() => setShowParentsGuideMedia(true)}
            startIcon={<FamilyRestroomOutlinedIcon />} sx={buttonSx}
          >
            Parents Guide
          </Button>
        </Box>
      </Box>

      <SearchMedia
        open={showSearchMedia}
        onClose={() => setShowSearchMedia(false)}
      />

      <ParentsGuideMedia
        open={showParentsGuideMedia}
        onClose={() => setShowParentsGuideMedia(false)}
      />
    </>
  );
};

export default MediaCard;